import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LayoutList, Pencil, Plus, Trash2 } from 'lucide-react';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import { useToast } from '../../contexts/ToastContext';
import {
  selectUnsubscribeGroups,
  selectUnsubscribeGroupsLoading,
  selectUnsubscribeGroupsError,
  selectUnsubscribeGroupsSuccess,
} from '../../store/unsubscribeGroups';
import {
  getUnsubscribeGroupsRequest,
  createUnsubscribeGroupRequest,
  updateUnsubscribeGroupRequest,
  deleteUnsubscribeGroupRequest,
  clearUnsubscribeGroupState,
} from '../../store/unsubscribeGroups';
import {
  IUnsubscribeGroup,
  CreateUnsubscribeGroupRequest,
} from '../../types/unsubscribeGroups/unsubscribeGroups.types';

const UnsubscribeGroups: React.FC = () => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const unsubscribeGroups = useSelector(selectUnsubscribeGroups);
  const loading = useSelector(selectUnsubscribeGroupsLoading);
  const error = useSelector(selectUnsubscribeGroupsError);
  const success = useSelector(selectUnsubscribeGroupsSuccess);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<IUnsubscribeGroup | null>(
    null
  );
  const [editingGroup, setEditingGroup] = useState<IUnsubscribeGroup | null>(
    null
  );
  const [lastOperation, setLastOperation] = useState<
    'create' | 'update' | 'delete' | null
  >(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    // isDefault: false,
    isPublic: true,
  });
  const [previewSelections, setPreviewSelections] = useState<
    Record<string, boolean>
  >({});

  // Fetch unsubscribe groups on component mount
  useEffect(() => {
    dispatch(getUnsubscribeGroupsRequest());
  }, [dispatch]);

  // Handle success state and show success toast
  useEffect(() => {
    if (success && lastOperation) {
      let message = '';
      switch (lastOperation) {
        case 'create':
          message = 'Unsubscribe group created successfully.';
          break;
        case 'update':
          message = 'Unsubscribe group updated successfully.';
          break;
        case 'delete':
          message = 'Unsubscribe group deleted successfully.';
          break;
      }

      showToast({
        type: 'success',
        title: 'Success!',
        message,
      });

      // Only close modal and reset form for create/update operations
      if (lastOperation === 'create' || lastOperation === 'update') {
        setIsModalOpen(false);
        setEditingGroup(null);
        setFormData({
          title: '',
          description: '',
          // isDefault: false,
          isPublic: true,
        });
      }

      setLastOperation(null);
      dispatch(clearUnsubscribeGroupState());
      dispatch(getUnsubscribeGroupsRequest());
    }
  }, [success, dispatch, lastOperation, showToast]);

  // Handle error state and show error toast
  useEffect(() => {
    if (error) {
      showToast({
        type: 'error',
        title: 'Error',
        message: error,
      });
      dispatch(clearUnsubscribeGroupState());
    }
  }, [error, showToast, dispatch]);

  // Initialize preview selections based on isDefault
  useEffect(() => {
    const initialSelections: Record<string, boolean> = {};
    unsubscribeGroups.forEach((group) => {
      initialSelections[group._id || ''] = true;
    });
    setPreviewSelections(initialSelections);
  }, [unsubscribeGroups]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSaveGroup = () => {
    if (formData.title.trim() && formData.description.trim()) {
      const groupData: CreateUnsubscribeGroupRequest = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        isDefault: false,
        isPublic: formData.isPublic,
      };

      if (editingGroup) {
        // Update existing group
        setLastOperation('update');
        showToast({
          type: 'info',
          title: 'Updating...',
          message: 'Please wait while we update the unsubscribe group.',
          duration: 2000,
        });
        dispatch(
          updateUnsubscribeGroupRequest({
            id: editingGroup._id!,
            data: groupData,
          })
        );
      } else {
        // Create new group
        setLastOperation('create');
        showToast({
          type: 'info',
          title: 'Creating...',
          message: 'Please wait while we create the unsubscribe group.',
          duration: 2000,
        });
        dispatch(createUnsubscribeGroupRequest(groupData));
      }
    }
  };

  const handleEditGroup = (group: IUnsubscribeGroup) => {
    setEditingGroup(group);
    setFormData({
      title: group.title,
      description: group.description,
      // isDefault: group.isDefault,
      isPublic: group.isPublic,
    });
    setIsModalOpen(true);
  };

  const handleDeleteClick = (group: IUnsubscribeGroup) => {
    setGroupToDelete(group);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (groupToDelete?._id) {
      setLastOperation('delete');
      showToast({
        type: 'info',
        title: 'Deleting...',
        message: 'Please wait while we delete the unsubscribe group.',
        duration: 2000,
      });
      dispatch(deleteUnsubscribeGroupRequest(groupToDelete._id));
      setIsDeleteModalOpen(false);
      setGroupToDelete(null);
    }
  };

  const handleAddNew = () => {
    setEditingGroup(null);
    setFormData({
      title: '',
      description: '',
      // isDefault: false,
      isPublic: true,
    });
    setIsModalOpen(true);
  };

  const handlePreviewCheckboxChange = (groupId: string) => {
    setPreviewSelections((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  return (
    <div className="p-6 space-y-6 bg-background">
      <div>
        <h1 className="text-2xl font-bold text-foreground">
          Unsubscribe Groups
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Organize and manage your unsubscribe groups for better organization
          and collaboration.
        </p>
      </div>

      <div className="flex gap-6">
        {/* Form Section - Left Side (50% width) */}
        <div className="w-1/2">
          <div className="bg-card rounded-xl border border-border p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-foreground mb-4">
              Unsubscribe Groups
            </h2>

            {unsubscribeGroups.length === 0 && !loading ? (
              /* Inline Form when no groups exist */
              <div className="space-y-4">
                <Input
                  label="Title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Enter group title"
                  required
                />
                <Input
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter group description"
                  required
                />

                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="isPublicInline"
                      name="isPublic"
                      checked={formData.isPublic}
                      onChange={handleInputChange}
                      className="h-4 w-4 rounded border-border accent-primary focus:ring-0"
                    />
                    <label
                      htmlFor="isPublicInline"
                      className="ml-2 block text-sm text-foreground"
                    >
                      Make this group public
                    </label>
                  </div>
                </div>

                <Button
                  onClick={handleSaveGroup}
                  className="w-full"
                  variant="primary"
                  disabled={
                    !formData.title.trim() ||
                    !formData.description.trim() ||
                    loading
                  }
                >
                  {loading ? 'Saving...' : 'Save'}
                </Button>
              </div>
            ) : (
              <>
                {/* Groups List */}
                <div className="mb-6">
                  {loading ? (
                    <div className="text-center py-4 text-muted-foreground text-sm">
                      Loading unsubscribe groups...
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {unsubscribeGroups.map((group) => (
                        <div
                          key={group._id}
                          className="bg-muted/50 rounded-lg border border-border p-4"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <h3 className="font-medium text-foreground mb-1">
                                {group.title}
                              </h3>
                              <p className="text-sm text-muted-foreground">
                                {group.description}
                              </p>
                            </div>
                            <div className="flex gap-2 shrink-0">
                              <Button
                                variant="ghost"
                                size="small"
                                onClick={() => handleEditGroup(group)}
                                className="text-primary hover:text-primary-hover"
                              >
                                <Pencil className="w-4 h-4 mr-1.5" />
                                Edit
                              </Button>
                              <Button
                                variant="ghost"
                                size="small"
                                onClick={() => handleDeleteClick(group)}
                                className="text-destructive hover:bg-destructive/10"
                              >
                                <Trash2 className="w-4 h-4 mr-1.5" />
                                Delete
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <Button
                  onClick={handleAddNew}
                  className="w-full"
                  variant="outline"
                  size="medium"
                  disabled={loading}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Preview Section - Right Side (50% width) */}
        <div className="w-1/2">
          <div className="bg-card rounded-xl border border-border p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-foreground mb-4">
              Preview
            </h2>

            {unsubscribeGroups.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <div className="text-center">
                  <div className="w-14 h-14 bg-muted rounded-full flex items-center justify-center mx-auto mb-3">
                    <LayoutList className="w-7 h-7 text-muted-foreground" />
                  </div>
                  <h3 className="text-base font-semibold text-card-foreground mb-1">
                    No groups to preview
                  </h3>
                  <p className="text-muted-foreground text-sm max-w-xs">
                    Create an unsubscribe group in the left panel to see how it
                    will appear to subscribers.
                  </p>
                </div>
              </div>
            ) : (
              <>
                <div className="space-y-4 mb-6">
                  {unsubscribeGroups.map((group) => (
                    <div key={group._id} className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id={`preview-${group._id}`}
                        checked={previewSelections[group._id || ''] || false}
                        onChange={() =>
                          handlePreviewCheckboxChange(group._id || '')
                        }
                        className="mt-1 h-4 w-4 rounded border-border accent-primary focus:ring-0 shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <label
                          htmlFor={`preview-${group._id}`}
                          className="block cursor-pointer"
                        >
                          <div className="font-medium text-foreground">
                            {group.title}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {group.description}
                          </div>
                        </label>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex gap-3">
                  <Button className="flex-1" variant="primary" size="medium">
                    Save preferences
                  </Button>
                  <Button className="flex-1" variant="outline" size="medium">
                    Unsubscribe from all
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Add/Edit Group Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={
          editingGroup ? 'Edit Unsubscribe Group' : 'Add New Unsubscribe Group'
        }
        size="medium"
      >
        <div className="space-y-4">
          <Input
            label="Title"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            placeholder="Enter group title"
            required
          />
          <Input
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="Enter group description"
            required
          />

          <div className="space-y-3">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isPublic"
                name="isPublic"
                checked={formData.isPublic}
                onChange={handleInputChange}
                className="h-4 w-4 rounded border-border accent-primary focus:ring-0"
              />
              <label
                htmlFor="isPublic"
                className="ml-2 block text-sm text-foreground"
              >
                Make this group public
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-border">
            <Button
              onClick={() => setIsModalOpen(false)}
              variant="outline"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveGroup}
              variant="primary"
              disabled={
                !formData.title.trim() ||
                !formData.description.trim() ||
                loading
              }
            >
              {loading ? 'Saving...' : editingGroup ? 'Update' : 'Save'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete confirmation modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setGroupToDelete(null);
        }}
        title="Delete unsubscribe group"
      >
        <p className="text-muted-foreground mb-6">
          Are you sure you want to delete &quot;{groupToDelete?.title}&quot;?
          This action cannot be undone.
        </p>
        <div className="flex justify-end gap-3 pt-4 border-t border-border">
          <Button
            variant="outline"
            onClick={() => {
              setIsDeleteModalOpen(false);
              setGroupToDelete(null);
            }}
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete}>
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default UnsubscribeGroups;
